import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Announcement from "./Announcement";
import allActions from "../../../Store/action";

// Persistent dashboard-wide banner that surfaces calendar integrations whose
// stored token has failed a recent health probe. Reads from the shared
// integrationHealth slice populated by Store/dashboard/action#getIntegrationHealth.
//
// Each connected provider is probed once per session (cached for 5 minutes on
// the client so route changes don't re-hit the endpoint). Dismissals are
// per-session via sessionStorage — the banner returns on next login.
const PROBE_PROVIDERS = ["google", "outlook", "calendly"];
const PROBE_TTL_MS = 5 * 60 * 1000;
const PROVIDER_LABEL = {
	google: "Google Calendar",
	outlook: "Outlook Calendar",
	calendly: "Calendly",
};

const isConnected = (loginData, provider) => {
	if (provider === "google") return Boolean(loginData?.isGoogleAuth);
	if (provider === "outlook") return Boolean(loginData?.isOutlookAuth);
	if (provider === "calendly") return Boolean(loginData?.isCalendlyAuth);
	return false;
};

const IntegrationDisconnectBanner = () => {
	const dispatch = useDispatch();
	const loginData = useSelector((s) => s.user?.LoginData);
	const integrationHealth = useSelector((s) => s.dashboard?.integrationHealth);
	const [dismissedTick, setDismissedTick] = useState(0);

	useEffect(() => {
		if (!loginData?.isTaxPro) return;
		PROBE_PROVIDERS.forEach((provider) => {
			if (!isConnected(loginData, provider)) return;
			const slice = integrationHealth?.[provider];
			const lastChecked = slice?.last_checked_at
				? new Date(slice.last_checked_at).getTime()
				: 0;
			const fresh = Date.now() - lastChecked < PROBE_TTL_MS;
			if (slice?.state === "loading" || fresh) return;
			dispatch(allActions.dashboard.getIntegrationHealth(provider));
		});
		// LoginData identity is stable until re-fetch; we only want to react to
		// auth-state changes, not every health-slice tick. integrationHealth is
		// intentionally read but not in the deps to avoid re-probing on each
		// dispatch result.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		loginData?.isTaxPro,
		loginData?.isGoogleAuth,
		loginData?.isOutlookAuth,
		loginData?.isCalendlyAuth,
	]);

	// Computed inline rather than memoized: the work is trivial (a tiny loop
	// over PROBE_PROVIDERS) and `dismissedTick` participates in the React
	// render cycle naturally so dismissals re-evaluate. Reading the unused
	// state value is just to keep the lint hook-deps rule happy.
	void dismissedTick;
	let brokenProvider = null;
	if (loginData?.isTaxPro) {
		for (const provider of PROBE_PROVIDERS) {
			if (!isConnected(loginData, provider)) continue;
			const status = integrationHealth?.[provider]?.status;
			if (status === "expired" || status === "revoked") {
				if (sessionStorage.getItem(`disconnectBanner:${provider}`) === "dismissed") {
					continue;
				}
				brokenProvider = provider;
				break;
			}
		}
	}

	if (!brokenProvider) return null;

	const handleReconnect = () => {
		// Clicking Reconnect also dismisses the banner for the session. The
		// Link still navigates to /dashboard/settings; if the tax pro actually
		// reconnects, the next health probe returns 'ok' and the banner won't
		// reappear regardless. If they bail out, the banner is back next login.
		sessionStorage.setItem(`disconnectBanner:${brokenProvider}`, "dismissed");
		setDismissedTick((n) => n + 1);
	};

	return (
		<Announcement
			condition={true}
			buttonEnabled={true}
			alertClass="warning d-flex justify-content-between py-2"
			mainClass="clearfix w-100 mx-auto"
			containerClass="container"
			title={`Your ${PROVIDER_LABEL[brokenProvider]} integration is disconnected.`}
			details={`Appointments won't sync until you reconnect.`}
			buttonLink="/dashboard/settings"
			buttonText="Reconnect"
			buttonClass="badge bg-dark py-2 text-decoration-none px-3 me-2"
			clickHandler={handleReconnect}
		/>
	);
};

export default IntegrationDisconnectBanner;
